import React, { createContext, useState, useContext, useEffect } from 'react';
import rolesData from '../Utils/roles.json';
import { loginUser } from '../Utils/apiServices';

// Create the Auth Context
const AuthContext = createContext(null);

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const getCustomUsers = () => {
    try {
      return JSON.parse(localStorage.getItem('customUsers')) || [];
    } catch (err) {
      console.error('Error parsing custom users:', err);
      return [];
    }
  };

  // Persist custom users
  const setCustomUsers = (users) => {
    localStorage.setItem('customUsers', JSON.stringify(users));
  };

  // Check for existing auth state in localStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('authUser');
    const storedAuth = localStorage.getItem('isAuthenticated');
    const accessToken = localStorage.getItem('accessToken');
    
    if (storedUser && storedAuth === 'true' && accessToken) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('authUser');
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      }
    } else {
      // Clear invalid auth state
      localStorage.removeItem('authUser');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    }
    setLoading(false);
  }, []);

  // Login function - API-based authentication
  const login = async (email, password) => {
    try {
      const response = await loginUser({ email, password });
      
      // Handle nested response structure from interceptor
      // API returns: { success: true, data: { user, accessToken, refreshToken } }
      // Interceptor wraps it: { success: true, data: { success: true, data: { user, accessToken, refreshToken } } }
      let responseData = response.data;
      
      // Unwrap nested data if needed
      if (responseData?.data?.data) {
        responseData = responseData.data;
      }
      
      if (response.success && responseData?.data) {
        const { user, accessToken, refreshToken } = responseData.data;
        
        // Store tokens
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        
        // Store user data
        const userInfo = {
          id: user.id,
          email: user.email,
          role: user.role,
          first_name: user.first_name,
          last_name: user.last_name,
          ...user, // Store full user object
        };
        
        setUser(userInfo);
        setIsAuthenticated(true);
        
        // Store in localStorage
        localStorage.setItem('authUser', JSON.stringify(userInfo));
        localStorage.setItem('isAuthenticated', 'true');
        
        return { success: true, user: userInfo };
      } else {
        return { 
          success: false, 
          error: response.error || 'Invalid email or password' 
        };
      }
    } catch (error) {
      return { 
        success: false, 
        error: error.error || 'Unable to login. Please try again.' 
      };
    }
  };

  // Register function (stores user locally as student by default)
  const register = (username, password, role = 'student') => {
    const trimmedUsername = (username || '').trim().toLowerCase();
    if (!trimmedUsername || !password) {
      return { success: false, error: 'Username and password are required' };
    }

    const customUsers = getCustomUsers();
    const allUsers = [...customUsers, ...rolesData];

    const exists = allUsers.some(
      (u) => (u.username || '').toLowerCase() === trimmedUsername
    );
    if (exists) {
      return { success: false, error: 'Username already exists' };
    }

    const newUser = { username: trimmedUsername, password, role };
    const updatedUsers = [...customUsers, newUser];
    setCustomUsers(updatedUsers);

    const userInfo = { username: trimmedUsername, role };
    setUser(userInfo);
    setIsAuthenticated(true);
    localStorage.setItem('authUser', JSON.stringify(userInfo));
    localStorage.setItem('isAuthenticated', 'true');

    return { success: true, user: userInfo };
  };

  // Logout function
  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    
    // Clear localStorage
    localStorage.removeItem('authUser');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  };

  // Check if user has specific role
  const hasRole = (role) => {
    return user?.role === role;
  };

  // Check if user has any of the specified roles
  const hasAnyRole = (roles) => {
    return roles.includes(user?.role);
  };

  // Get current user role
  const getUserRole = () => {
    return user?.role || null;
  };

  // Update user function - updates both state and localStorage
  const updateUser = (updatedUserData) => {
    const updatedUser = {
      ...user,
      ...updatedUserData,
    };
    setUser(updatedUser);
    localStorage.setItem('authUser', JSON.stringify(updatedUser));
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    register,
    logout,
    hasRole,
    hasAnyRole,
    getUserRole,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;

